import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { DateSelector } from '../ServiceBookingForm/DateSelector/DateSelector';
import { DoctorList } from '../ServiceBookingForm/DoctorList/DoctorList';
import { useDoctorSelect } from './useDoctorSelect';
import { useBranchSelect } from './useBranchSelect';
import { useSyncSelectedService } from './useSyncSelectedService';
import { useSyncSelectedDoctor } from './useSyncSelectedDoctor';
import { useSyncSelectedBranch } from './useSyncSelectedBranch';
import { ServiceDescription } from '../ServiceBookingForm/ServiceDescription/ServiceDescription';
import { useGlobalBackButton } from '../../GlobalBackButtonContext';
import {
    getBranches,
    getDoctorsMetadata,
    getFullServicesFromServicesData,
    getSelectedDate,
    getServiceSelected,
    getServicesInfoLoaded,
} from '../store/selectors';
import {
    fetchServicesTimeslots,
    fetchServicesTimetable,
    selectBranchSimplified,
    selectDoctorSimplified,
    selectServiceSimplified,
} from '../store/serviceSlice';
import { getTimetableInfoSafe, ServiceBookingContainer } from '../ServiceBookingContainer';
import { sortBy } from '../../utils';
import { GroupInput } from '../../components/GroupInput/GroupInput';

const styles = require('b_').with('service-booking-selector');

const START_TO_TYPE = 'Начните вводить данные';

// eslint-disable-next-line max-statements
export const DoctorServiceBooking = ({
    doctorName,
    serviceBranchId,
    serviceId,
    baseUrl,
    onBack,
    serviceName,
    specialityName,
    targetDoctorId,
}) => {
    const { setOnBack } = useGlobalBackButton();
    const dispatch = useDispatch();
    const allServices = useSelector(getFullServicesFromServicesData);
    const allDoctors = useSelector(getDoctorsMetadata);
    const branches = useSelector(getBranches);
    const selectedInfo = useSelector(getServiceSelected);
    const isLoaded = useSelector(getServicesInfoLoaded);
    const date = useSelector(getSelectedDate);
    const [ selectedDoctorId, setSelectedDoctorId ] = useState();
    const [ servicesList, setServicesList ] = useState([]);
    const [ selectedBranchId, setSelectedBranchId ] = useState('');
    const [ branchList, setBranchList ] = useState([]);
    const [ selectedServiceId, setSelectedServiceId ] = useState('');
    const [ doctorsList, setDoctorsList ] = useState([]);

    const onSelectService = (sId) => {
        setSelectedServiceId(sId);
        dispatch(selectServiceSimplified(sId));
    };

    const onSelectDoctorHandler = (docId) => {
        setSelectedDoctorId(docId);
        dispatch(selectDoctorSimplified(docId));
    };

    const onSelectBranchHandler = (bId) => {
        setSelectedBranchId(bId);
        dispatch(selectBranchSimplified(bId));
    };

    const selectedService = allServices?.find(({ id }) => id === selectedServiceId);

    const loadTimetable = useCallback(
        async (doc) => {
            const info = await getTimetableInfoSafe(doc, selectedBranchId, selectedDoctorId);
            if (info?.assignment) {
                dispatch(
                    fetchServicesTimetable({
                        baseUrl,
                        ...info,
                    }),
                );
            }
        },
        [ selectedBranchId, selectedDoctorId ],
    );

    useEffect(() => {
        if (selectedBranchId && selectedDoctorId) {
            const doc = allDoctors?.find(({ doctorId }) => doctorId === selectedDoctorId);
            if (doc) {
                loadTimetable(doc);
            }
        }
    }, [ selectedBranchId, selectedDoctorId ]);

    useEffect(() => {
        setOnBack(() => onBack);
        return () => setOnBack(null);
    }, [ onBack, setOnBack ]);

    useEffect(() => {
        setDoctorsList(sortBy(allDoctors, 'name'));
    }, [allDoctors?.length]);

    useEffect(() => {
        if (selectedBranchId && selectedServiceId) {
            const request = {
                baseUrl,
                date,
                serviceId: selectedServiceId,
                branchId: selectedBranchId,
            };
            dispatch(fetchServicesTimeslots(request));
        }
    }, [ selectedBranchId, selectedServiceId ]);

    useSyncSelectedService({
        selectedInfo,
        selectedServiceId,
        onSelectServiceHandler: onSelectService,
    });

    useSyncSelectedDoctor({
        selectedInfo,
        selectedDoctorId,
        onSelectDoctorHandler,
    });

    useSyncSelectedBranch({
        selectedInfo,
        selectedBranchId,
        onSelectBranchHandler,
    });

    useDoctorSelect({
        selectedDoctorId,
        selectedInfo,
        selectedBranchId,
        selectedServiceId,
        setBranchList,
        setServicesList,
        setSelectedBranchId: onSelectBranchHandler,
        setSelectedServiceId: onSelectService,
        allDoctors,
        branches,
        allServices,
        dispatch,
        selectBranch: selectBranchSimplified,
        selectService: selectServiceSimplified,
    });

    useBranchSelect({
        selectedBranchId,
        selectedDoctorId,
        selectedServiceId,
        setDoctorsList,
        setServicesList,
        setSelectedDoctorId: onSelectDoctorHandler,
        setSelectedServiceId: onSelectService,
        allDoctors,
        allServices,
        dispatch,
        selectDoctor: selectDoctorSimplified,
        selectService: selectServiceSimplified,
        selectedInfo,
    });

    React.useEffect(() => {
        if (isLoaded && allDoctors && (doctorName || targetDoctorId) && !selectedDoctorId) {
            const found = doctorName
                ? allDoctors?.find(
                    ({ name, specialityName: _specialityName }) => name?.toLowerCase() === doctorName?.toLowerCase() &&
                        (!specialityName || specialityName?.toLowerCase() === _specialityName?.toLowerCase()),
                )
                : allDoctors?.find(({ doctorId }) => doctorId === targetDoctorId);
            if (found) {
                onSelectDoctorHandler(found?.doctorId);
            }
        }
    }, [ allDoctors, isLoaded, doctorName, targetDoctorId, specialityName ]);

    const branchPreselected = React.useRef(false);
    React.useEffect(() => {
        if (!branchPreselected.current && serviceBranchId && selectedDoctorId) {
            const foundB = branchList?.find(({ id }) => id === serviceBranchId);
            if (foundB) {
                onSelectBranchHandler(foundB?.id);
                branchPreselected.current = true;
            }
        }
    }, [ branchList, serviceBranchId, selectedDoctorId ]);

    const servicePreselected = React.useRef(false);
    React.useEffect(() => {
        if (!servicePreselected.current && (serviceId || serviceName) && selectedDoctorId && selectedBranchId) {
            const normalizeText = text => text
                ?.toLowerCase()
                .replace(/["“”«»]/g, '')
                .replace(/\s+/g, ' ')
                .trim();

            const foundS = serviceId
                ? servicesList?.find(({ id }) => id === serviceId)
                : servicesList?.find(({ name }) => normalizeText(name) === normalizeText(serviceName));
            if (foundS) {
                onSelectService(foundS?.id);
                servicePreselected.current = true;
            }
        }
    }, [ servicesList, serviceId, serviceName, selectedDoctorId, selectedBranchId ]);

    return (
        <>
            <ServiceBookingContainer skipInitialDataLoad={true} skipTimetableAutoLoad/>
            <div className={styles('', { top: true })}>
                <GroupInput
                    id="doctor"
                    type="selector"
                    label="Специалист"
                    placeholder={START_TO_TYPE}
                    metaData={doctorsList}
                    value={selectedDoctorId}
                    onChange={onSelectDoctorHandler}
                />
                <GroupInput
                    id="branch"
                    type="selector"
                    label="Филиал"
                    metaData={branchList}
                    value={selectedBranchId}
                    onChange={onSelectBranchHandler}
                    placeholder={START_TO_TYPE}
                />
                <GroupInput
                    id="service"
                    type="selector"
                    label="Услуга"
                    metaData={servicesList}
                    value={selectedServiceId}
                    onChange={onSelectService}
                    placeholder={START_TO_TYPE}
                />
                <ServiceDescription selectedService={selectedService}
                    emptyDescription={<div style={{ marginTop: 48 }}/>}/>
                {selectedBranchId && selectedServiceId && selectedDoctorId ? (
                    <div style={{ marginTop: -24 }}>
                        <DateSelector/>
                        <DoctorList/>
                    </div>
                ) : null}
            </div>
        </>
    );
};
